import Jobs from '@/api/jobs'
import JobHelper from '@/helpers/job'
import {toCurrency} from '@/helpers/currency-helper'

export default {
  namespaced: true,
  state: {
    jobs: [],
    currentJob: null,
    currentApplication: null,
    currentOffer: null,
    jobBoards: [],
    isJobBoardOwner: false,
    jobBoardsList: [],
    jobLoading: false,
    keyword: '',
    locations: '',
    jobType: '',
    salaryRange: '',
    jobCategory: '--all--',
    ecoSystem: 'all',
    currentJobMin: null,
    currentJobMax: null,
    currentJobRewardPer: null,
    minCharity: null,
    maxCharity: null,
    charityRewardPer: null,
    currency: 'USD',
    activeNav: '',
    jobTags: {},
    createJobTrigger: 1
  },
  getters: {
    currentJob: state => state.currentJob,
    currentApplication: state => state.currentApplication,
    loadedApplication: state => {
      return state.currentApplication !== null
    },
    currentOffer: state => state.currentOffer,
    loadedOffer: state => {
      return state.currentOffer !== null
    },
    jobById: (state, getters) => (id) => {
      return state.jobs.find(job => job.id === id)
    },
    getFilters: (state) => {
      return {
        keyword: state.keyword,
        locations: state.locations,
        jobType: state.jobType,
        salaryRange: state.salaryRange,
        jobCategory: state.jobCategory,
        ecoSystem: state.ecoSystem === 'all' ? '' : state.ecoSystem
      }
    }
  },
  mutations: {
    setCreateJobTrigger (state) {
      state.createJobTrigger = state.createJobTrigger + 1
    },
    setJobTags (state, jobTags) {
      state.jobTags = jobTags
    },
    clearRewards (state) {
      state.currentJobMin = null
      state.currentJobMax = null
      state.currentJobRewardPer = null
      state.minCharity = null
      state.maxCharity = null
      state.charityRewardPer = null
    },
    setCurrentJobMin (state, {currentJobMin, currency}) {
      state.currency = currency
      if (currentJobMin) {
        state.minCharity = toCurrency(parseFloat(JobHelper.getSeventeenPercent(currentJobMin)), currency)
      }
      state.currentJobMin = toCurrency(parseFloat(JobHelper.getHalf(currentJobMin)), currency)
    },
    setCurrentJobMax (state, {currentJobMax, currency}) {
      if (currentJobMax) {
        state.maxCharity = toCurrency(parseFloat(JobHelper.getSeventeenPercent(currentJobMax)), currency)
      }
      state.currentJobMax = toCurrency(parseFloat(JobHelper.getHalf(currentJobMax)), currency)
    },
    setCurrentJobRewardPer (state, currentJobRewardPer) {
      if (currentJobRewardPer) {
        state.charityRewardPer = JobHelper.getSeventeenPercent(currentJobRewardPer)
      }
      state.currentJobRewardPer = (currentJobRewardPer / 2).toFixed(2)
    },
    setCurrentJob (state, to) {
      state.currentJob = to
    },
    setApplication (state, to) {
      state.currentApplication = to
    },
    deleteApplication (state, to) {
      state.currentApplication = null
    },
    setOffer (state, to) {
      state.currentOffer = to
    },
    setJobs (state, jobs) {
      state.jobs = jobs
    },
    resetJobs (state) {
      state.jobs = []
    },
    resetCurrentJob (state) {
      state.currentJob = null
      state.currentApplication = null
      state.currentOffer = null
    },
    setJobBoards (state, jobBoards) {
      state.jobBoards = [...jobBoards]
    },
    setJobBoardOwner (state, isJobBoardOwner) {
      state.isJobBoardOwner = isJobBoardOwner > 0
    },
    setJobBoardsList (state, jobBoardsList) {
      state.jobBoardsList = jobBoardsList
    },
    setJobLoading (state, jobLoading) {
      state.jobLoading = jobLoading
    },
    setKeyword (state, keyword) {
      state.keyword = keyword
    },
    setLocations (state, locations) {
      state.locations = locations
    },
    setJobType (state, jobType) {
      state.jobType = jobType
    },
    setSalaryRange (state, salaryRange) {
      state.salaryRange = salaryRange
    },
    setJobCategory (state, jobCategory) {
      state.jobCategory = jobCategory
    },
    setEcoSystem (state, ecoSystem) {
      state.ecoSystem = ecoSystem || 'all'
    },
    resetFilters (state) {
      state.keyword = ''
      state.locations = ''
      state.jobType = ''
      state.salaryRange = ''
      state.jobCategory = '--all--'
      state.ecoSystem = 'all'
    },
    setActiveNav (state, activeNav) {
      if (activeNav === 'all') {
        state.activeNav = ''
      } else if (activeNav === 'Closed') {
        state.activeNav = 'completed'
      } else {
        state.activeNav = activeNav
      }
    }
  },
  actions: {
    async getAppliedJobs ({ commit }, { keyword, locations } = {}) {
      const jobs = await Jobs.applied({ keyword, locations })
      commit('setJobs', jobs)
    },
    async getHiredJobs ({ commit }, { keyword, locations } = {}) {
      const jobs = await Jobs.hired({ keyword, locations })
      commit('setJobs', jobs)
    },
    async getJobs ({ commit }, { keyword, locations } = {}) {
      commit('setJobLoading', true)
      const jobs = await Jobs.search({ keyword, locations })
      commit('setJobs', jobs)
      commit('setJobLoading', false)
    },
    async getJob ({ commit, getters }, jobId) {
      const job = await Jobs.get(jobId)
      commit('setCurrentJob', job)
    },
    async getJobUpdate ({ commit }, jobId) {
      const job = await Jobs.get(jobId)
      commit('setCurrentJob', job)
    },
    async setApplication ({ commit }, application) {
      commit('setApplication', application)
    },
    async getApplication ({ commit, getters }, jobId) {
      if (!getters.loadedApplication) {
        const application = await Jobs.application(jobId)
        commit('setApplication', application)
      }
    },
    async deleteApplication ({ commit }, jobId) {
      const application = await Jobs.deleteApplication(jobId)
      commit('deleteApplication', application)
    },
    async getOffer ({ commit, getters }, jobId) {
      if (!getters.loadedOffer) {
        const offer = await Jobs.offer(jobId)
        commit('setOffer', offer)
      }
    },
    async rejectOffer ({ commit }, jobId) {
      const offer = await Jobs.rejectOffer(jobId)
      commit('setOffer', offer)
    },
    async acceptOffer ({ commit }, jobId) {
      const offer = await Jobs.acceptOffer(jobId)
      commit('setOffer', offer)
    },
    async jobViewed (data, jobId) {
      await Jobs.viewed(jobId)
    },
    async search ({commit, state}, {keyword, locations, jobType, salaryRange, jobCategory, ecoSystem} = {}) {
      commit('setJobLoading', true)
      const jobs = await Jobs.search({ keyword, locations, jobType, salaryRange, jobCategory, ecoSystem, status: state.activeNav })
      let filteredJobs
      if (state.activeNav === 'completed') {
        filteredJobs = jobs
      } else {
        filteredJobs = jobs.filter(job => job.status !== 'completed')
      }
      commit('setJobs', filteredJobs)
      commit('setJobLoading', false)
    },
    async getJobBoards ({commit}) {
      const jobBoards = await Jobs.getJobBoards()
      commit('setJobBoardOwner', jobBoards.length)
      commit('setJobBoards', jobBoards)
    },
    async getJobSeekersBoardList ({commit}) {
      const jobBoardsList = await Jobs.getJobBoardList()
      commit('setJobBoardsList', jobBoardsList.data)
    },
    async getJobTags ({commit}) {
      try {
        const jobTags = await Jobs.getJobTags()
        commit('setJobTags', jobTags)
      } catch (e) {
        console.log('error action setJobTags', e)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/jobs.js